import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, Check, AlertCircle } from "lucide-react";

const MIN_LEN = 8;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? null : "This reset link is missing its token. Please request a new one.");

  const rules = useMemo(
    () => [
      { key: "len", label: `At least ${MIN_LEN} characters`, ok: pw1.length >= MIN_LEN },
      { key: "match", label: "Both passwords match", ok: pw1.length > 0 && pw1 === pw2 },
    ],
    [pw1, pw2],
  );
  const allValid = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setBusy(true);
    setError(null);
    try {
      await auth.resetPassword(token, pw1);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "This reset link is invalid or has expired. Request a new one to continue.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F9FAF7] flex items-center justify-center p-4">
        <div
          data-testid="reset-success"
          className="w-full max-w-md bg-white rounded-3xl border border-[#B7CCB1] shadow-[0_4px_14px_rgba(0,0,0,0.05)] p-7 sm:p-8 text-center fade-in-up"
        >
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[#4F6F52] text-white flex items-center justify-center mb-3 shadow-[0_4px_14px_rgba(79,111,82,0.22)]">
            <Check size={20} strokeWidth={2.6} />
          </div>
          <h1 className="font-display text-2xl text-[#2E2E2E] mb-1.5">Password updated</h1>
          <p className="text-[15px] text-[#4E5A4C] leading-relaxed mb-6">
            Your password has been updated. You can now sign in.
          </p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            data-testid="reset-go-to-signin"
            className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3.5 font-semibold transition shadow-[0_4px_14px_rgba(79,111,82,0.22)]"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  const linkBroken = !!error && (!token || /invalid|expired|already/i.test(error));

  return (
    <div className="min-h-screen bg-[#F9FAF7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#2E4531] hover:text-[#1F2F22] rounded-full pl-1.5 pr-3 py-1.5 hover:bg-[#E1ECDD] transition mb-4"
        >
          <ArrowLeft size={15} strokeWidth={2.4} /> Back to sign in
        </Link>

        <div className="bg-white rounded-3xl border border-[#DCE5DB] shadow-[0_4px_14px_rgba(0,0,0,0.05)] p-7 sm:p-8 fade-in-up">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="w-9 h-9 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <KeyRound size={17} strokeWidth={1.8} />
            </span>
            <h1 className="font-display text-2xl text-[#2E2E2E]">Choose a new password</h1>
          </div>

          {error && (
            <div
              data-testid="reset-error"
              className="mt-3 mb-4 flex items-start gap-2.5 rounded-2xl bg-[#F5E6D3]/60 border border-[#D6C7A1]/70 p-4"
            >
              <AlertCircle size={16} className="text-[#A86B4A] shrink-0 mt-0.5" />
              <div className="text-sm text-[#5A4230] leading-relaxed">
                <p>{error}</p>
                {linkBroken && (
                  <Link
                    to="/forgot-password"
                    data-testid="reset-request-new"
                    className="inline-block mt-2 text-[#3F5A42] font-semibold underline hover:text-[#2E4531]"
                  >
                    Send me a new reset link
                  </Link>
                )}
              </div>
            </div>
          )}

          {!linkBroken && (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-sm text-[#6B6B6B] mb-1.5 block">New password</span>
                <input
                  type="password"
                  required
                  autoFocus
                  data-testid="reset-pw1"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  placeholder={`At least ${MIN_LEN} characters`}
                  className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                />
              </label>
              <label className="block">
                <span className="text-sm text-[#6B6B6B] mb-1.5 block">Confirm new password</span>
                <input
                  type="password"
                  required
                  data-testid="reset-pw2"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                />
              </label>

              <ul data-testid="reset-rules" className="text-xs space-y-1">
                {rules.map((r) => (
                  <li
                    key={r.key}
                    className={`flex items-center gap-2 ${r.ok ? "text-[#3F5A42]" : "text-[#6B6B6B]"}`}
                  >
                    <span className={`inline-flex w-4 h-4 rounded-full items-center justify-center ${r.ok ? "bg-[#4F6F52] text-white" : "bg-[#EEF3EC] text-[#6B6B6B]"}`}>
                      {r.ok ? <Check size={10} strokeWidth={3} /> : <span className="w-1 h-1 rounded-full bg-current" />}
                    </span>
                    {r.label}
                  </li>
                ))}
              </ul>

              <button
                type="submit"
                disabled={!allValid || busy}
                data-testid="reset-submit"
                className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-3.5 font-semibold transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
              >
                {busy ? "Updating..." : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
